(function () {
  'use strict';

  /* =========================================================
     CONFIGURATION
  ========================================================= */

  const POST_LENGTH_SELECTOR =
    'fieldset.option-selector[data-option="Post Length"]';

  const PLACEHOLDER_VALUE = 'Select Option';

  const ADD_TO_CART_SELECTOR =
    '[name="add"], .product-form__submit';

  const PRODUCT_FORM_SELECTOR =
    'form[action*="/cart/add"]';


  /* =========================================================
     PAGE STATE

     Important:
     We intentionally keep this state OUTSIDE the fieldset.

     Your theme can replace/re-render the fieldset at runtime.
     If state were stored on the fieldset, it would be lost.
  ========================================================= */

  let userHasSelectedPostLength = false;
  let resetTimer = null;


  /* =========================================================
     HELPERS
  ========================================================= */

  function getPostLengthFieldsets() {
    return Array.from(
      document.querySelectorAll(POST_LENGTH_SELECTOR)
    );
  }


  function getPlaceholder(fieldset) {
    if (!fieldset) return null;

    return Array.from(
      fieldset.querySelectorAll('input[type="radio"]')
    ).find(function (radio) {
      return (
        (radio.value || '').trim().toLowerCase() ===
        PLACEHOLDER_VALUE.toLowerCase()
      );
    }) || null;
  }


  function getSelectedPostLength(fieldset) {
    if (!fieldset) return null;

    return fieldset.querySelector(
      'input[type="radio"]:checked'
    );
  }


  function isValidPostLength(fieldset) {
    if (!fieldset) {
      return true;
    }

    const selected =
      getSelectedPostLength(fieldset);

    if (!selected) {
      return false;
    }

    const value =
      (selected.value || '').trim();

    return (
      value !== '' &&
      value.toLowerCase() !==
        PLACEHOLDER_VALUE.toLowerCase()
    );
  }


  /* =========================================================
     FIND THE POST LENGTH FIELDSET FOR A BUTTON/FORM
  ========================================================= */

  function findPostLength(element) {
    if (!element) {
      return null;
    }

    /*
     * First try inside product form.
     */
    const form =
      element.closest &&
      element.closest(PRODUCT_FORM_SELECTOR);

    if (form) {
      const fieldset =
        form.querySelector(
          POST_LENGTH_SELECTOR
        );

      if (fieldset) {
        return fieldset;
      }
    }


    /*
     * Some themes place product options outside
     * the actual Shopify <form>.
     */
    const product =
      element.closest &&
      element.closest(
        'product-info, .product-info, .product, [data-product-id]'
      );

    if (product) {
      const fieldset =
        product.querySelector(
          POST_LENGTH_SELECTOR
        );

      if (fieldset) {
        return fieldset;
      }
    }


    /*
     * Product page fallback.
     */
    const fieldsets =
      getPostLengthFieldsets();

    if (fieldsets.length === 1) {
      return fieldsets[0];
    }

    return null;
  }


  /* =========================================================
     HIDE "SELECT OPTION"

     The radio stays in the DOM and stays checked internally,
     but its visible label is hidden.
  ========================================================= */

  function hidePlaceholder(fieldset) {
    const placeholder =
      getPlaceholder(fieldset);

    if (!placeholder) {
      return;
    }

    /*
     * Input is already visually hidden in your theme,
     * but make sure it can never appear.
     */
    placeholder.style.setProperty(
      'display',
      'none',
      'important'
    );


    /*
     * Your markup uses:
     *
     * <input id="...">
     * <label for="...">
     *
     * Find that exact label.
     */
    if (placeholder.id) {
      const labels =
        fieldset.querySelectorAll('label');

      labels.forEach(function (label) {
        if (
          label.getAttribute('for') ===
          placeholder.id
        ) {
          label.style.setProperty(
            'display',
            'none',
            'important'
          );

          label.setAttribute(
            'aria-hidden',
            'true'
          );
        }
      });
    }


    /*
     * Extra fallback.
     */
    const next =
      placeholder.nextElementSibling;

    if (
      next &&
      next.tagName === 'LABEL'
    ) {
      next.style.setProperty(
        'display',
        'none',
        'important'
      );
    }
  }


  /* =========================================================
     INITIAL EMPTY STATE

     Until the customer clicks a real Post Length:

     Select Option       = checked internally
     Flat Back 4.5 MM    = unchecked
     Flat Back 5.0 MM    = unchecked
     ...
  ========================================================= */

  function forceEmptyPostLength(fieldset) {
    if (
      !fieldset ||
      userHasSelectedPostLength
    ) {
      return;
    }

    const placeholder =
      getPlaceholder(fieldset);

    if (!placeholder) {
      return;
    }

    const radios =
      fieldset.querySelectorAll(
        'input[type="radio"]'
      );


    /*
     * Remove theme's automatic selection such as
     * Flat Back 4.5 MM.
     */
    radios.forEach(function (radio) {
      if (radio !== placeholder) {
        radio.checked = false;

        /*
         * Remove static HTML checked attribute too.
         */
        radio.removeAttribute(
          'checked'
        );
      }
    });


    /*
     * Internally select the placeholder.
     */
    placeholder.checked = true;

    hidePlaceholder(fieldset);
  }


  /* =========================================================
     ERROR MESSAGE
  ========================================================= */

  function getErrorElement(fieldset) {
    if (!fieldset) {
      return null;
    }

    /*
     * Check immediately after fieldset first.
     */
    let next =
      fieldset.nextElementSibling;

    if (
      next &&
      next.hasAttribute(
        'data-post-length-error'
      )
    ) {
      return next;
    }


    const error =
      document.createElement('div');

    error.setAttribute(
      'data-post-length-error',
      ''
    );

    error.setAttribute(
      'role',
      'alert'
    );

    error.textContent =
      'Please select the Post Length.';

    error.style.display = 'none';

    fieldset.insertAdjacentElement(
      'afterend',
      error
    );

    return error;
  }


  function showError(fieldset) {
    if (!fieldset) {
      return;
    }

    const error =
      getErrorElement(fieldset);

    if (error) {
      error.style.display =
        'block';
    }

    fieldset.classList.add(
      'post-length-invalid'
    );

    fieldset.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }


  function hideError(fieldset) {
    if (!fieldset) {
      return;
    }

    const next =
      fieldset.nextElementSibling;

    if (
      next &&
      next.hasAttribute(
        'data-post-length-error'
      )
    ) {
      next.style.display = 'none';
    }

    fieldset.classList.remove(
      'post-length-invalid'
    );
  }


  function validatePostLength(fieldset) {
    if (
      isValidPostLength(fieldset)
    ) {
      hideError(fieldset);

      return true;
    }

    showError(fieldset);

    return false;
  }


  /* =========================================================
     CRITICAL PART:
     DETECT USER CLICK BEFORE THE THEME PROCESSES IT

     We use pointerdown in capture phase.

     This runs BEFORE:
       - label click
       - radio change
       - variant JS
       - our automatic reset timer

     Therefore the customer's click can no longer be undone.
  ========================================================= */

  function detectManualPostLengthSelection(event) {
    if (!event.isTrusted) {
      return;
    }

    const fieldset =
      event.target.closest &&
      event.target.closest(
        POST_LENGTH_SELECTOR
      );

    if (!fieldset) {
      return;
    }


    let radio = null;


    /*
     * Direct input interaction.
     */
    if (
      event.target.matches &&
      event.target.matches(
        'input[type="radio"]'
      )
    ) {
      radio = event.target;
    }


    /*
     * The customer normally clicks the label
     * or <span class="js-value">.
     */
    if (!radio) {
      const label =
        event.target.closest &&
        event.target.closest('label');

      if (
        label &&
        fieldset.contains(label)
      ) {
        const inputId =
          label.getAttribute('for');

        if (inputId) {
          radio =
            document.getElementById(
              inputId
            );
        }
      }
    }


    if (!radio) {
      return;
    }


    const value =
      (radio.value || '').trim();


    /*
     * Ignore hidden Select Option.
     */
    if (
      value === '' ||
      value.toLowerCase() ===
        PLACEHOLDER_VALUE.toLowerCase()
    ) {
      return;
    }


    /*
     * THIS IS THE IMPORTANT LINE.
     *
     * Stop resetting BEFORE the browser processes
     * the actual radio click.
     */
    userHasSelectedPostLength =
      true;


    /*
     * Stop the watchdog immediately.
     */
    if (resetTimer) {
      clearInterval(
        resetTimer
      );

      resetTimer = null;
    }
  }


  /*
   * pointerdown happens before click/change.
   */
  document.addEventListener(
    'pointerdown',
    detectManualPostLengthSelection,
    true
  );


  /*
   * Fallback for older browsers/devices.
   */
  document.addEventListener(
    'mousedown',
    detectManualPostLengthSelection,
    true
  );


  /* =========================================================
     CHANGE EVENT

     Once the radio is actually checked:
       - remove error
       - enable Buy with Shop
  ========================================================= */

  document.addEventListener(
    'change',
    function (event) {
      const radio =
        event.target;

      if (
        !radio.matches ||
        !radio.matches(
          POST_LENGTH_SELECTOR +
          ' input[type="radio"]'
        )
      ) {
        return;
      }


      const fieldset =
        radio.closest(
          POST_LENGTH_SELECTOR
        );

      if (!fieldset) {
        return;
      }


      const value =
        (radio.value || '').trim();


      if (
        radio.checked &&
        value !== '' &&
        value.toLowerCase() !==
          PLACEHOLDER_VALUE.toLowerCase()
      ) {
        userHasSelectedPostLength =
          true;

        hideError(
          fieldset
        );

        updateShopPayBlockers();
      }
    },
    true
  );


  /* =========================================================
     ADD TO CART / UPCART VALIDATION
  ========================================================= */

  document.addEventListener(
    'click',
    function (event) {
      const button =
        event.target.closest &&
        event.target.closest(
          ADD_TO_CART_SELECTOR
        );

      if (!button) {
        return;
      }


      const fieldset =
        findPostLength(button);

      /*
       * Product without Post Length:
       * leave it alone.
       */
      if (!fieldset) {
        return;
      }


      if (
        !validatePostLength(
          fieldset
        )
      ) {
        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();

        return false;
      }
    },
    true
  );


  /* =========================================================
     PRODUCT FORM SUBMISSION VALIDATION

     Secondary protection for UpCart/theme scripts.
  ========================================================= */

  document.addEventListener(
    'submit',
    function (event) {
      const form =
        event.target;

      if (
        !form.matches ||
        !form.matches(
          PRODUCT_FORM_SELECTOR
        )
      ) {
        return;
      }


      const fieldset =
        findPostLength(form);

      if (!fieldset) {
        return;
      }


      if (
        !validatePostLength(
          fieldset
        )
      ) {
        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();

        return false;
      }
    },
    true
  );


  /* =========================================================
     BUY WITH SHOP VALIDATION
  ========================================================= */

  function updateShopPayBlockers() {
    document
      .querySelectorAll(
        '.shopify-payment-button'
      )
      .forEach(function (paymentContainer) {

        const fieldset =
          findPostLength(
            paymentContainer
          );

        if (!fieldset) {
          return;
        }


        if (
          window
            .getComputedStyle(
              paymentContainer
            )
            .position === 'static'
        ) {
          paymentContainer.style.position =
            'relative';
        }


        let blocker =
          paymentContainer.querySelector(
            '[data-post-length-shop-blocker]'
          );


        if (!blocker) {
          blocker =
            document.createElement(
              'div'
            );

          blocker.setAttribute(
            'data-post-length-shop-blocker',
            ''
          );

          blocker.addEventListener(
            'click',
            function (event) {

              /*
               * If valid, don't interfere.
               */
              if (
                isValidPostLength(
                  fieldset
                )
              ) {
                return;
              }

              event.preventDefault();

              event.stopPropagation();

              event.stopImmediatePropagation();

              showError(
                fieldset
              );
            },
            true
          );

          paymentContainer.appendChild(
            blocker
          );
        }


        /*
         * Overlay exists only while invalid.
         */
        blocker.style.display =
          isValidPostLength(fieldset)
            ? 'none'
            : 'block';
      });
  }


  /* =========================================================
     CSS
  ========================================================= */

  function injectStyles() {
    if (
      document.getElementById(
        'post-length-validation-styles'
      )
    ) {
      return;
    }


    const style =
      document.createElement('style');

    style.id =
      'post-length-validation-styles';


    style.textContent = `

      /*
       * Hide Select Option.
       */
      fieldset.option-selector[data-option="Post Length"]
      input[type="radio"][value="Select Option"] {
        display: none !important;
      }


      fieldset.option-selector[data-option="Post Length"]
      input[type="radio"][value="Select Option"] + label {
        display: none !important;
      }


      /*
       * Error message.
       */
      [data-post-length-error] {
        display: none;
        color: #d00000;
        font-size: 14px;
        line-height: 1.4;
        margin-top: 12px;
        margin-bottom: 12px;
      }


      /*
       * Post Length heading becomes red
       * after invalid Add to Cart attempt.
       */
      fieldset.option-selector[data-option="Post Length"].post-length-invalid
      > legend {
        color: #d00000 !important;
      }


      /*
       * Invisible Buy with Shop blocker.
       */
      [data-post-length-shop-blocker] {
        position: absolute !important;
        inset: 0 !important;
        width: 100% !important;
        height: 100% !important;
        z-index: 99999 !important;
        background: transparent !important;
        cursor: pointer !important;
      }

    `;


    document.head.appendChild(
      style
    );
  }


  /* =========================================================
     INITIALIZATION
  ========================================================= */

  function initializePostLength() {

    getPostLengthFieldsets()
      .forEach(function (fieldset) {

        /*
         * Always hide placeholder.
         */
        hidePlaceholder(
          fieldset
        );


        /*
         * ONLY reset before customer interaction.
         */
        if (
          !userHasSelectedPostLength
        ) {
          forceEmptyPostLength(
            fieldset
          );
        }

      });


    updateShopPayBlockers();
  }


  /* =========================================================
     STARTUP
  ========================================================= */

  function start() {
    console.log(
      '[Post Length Validation] loaded'
    );


    injectStyles();

    initializePostLength();


    /*
     * Your theme selects Flat Back 4.5 MM sometime
     * after page load.
     *
     * Correct that until the customer starts interacting.
     */
    resetTimer =
      setInterval(function () {

        /*
         * Critical:
         * after customer's first real Post Length click,
         * this interval permanently stops.
         */
        if (
          userHasSelectedPostLength
        ) {
          clearInterval(
            resetTimer
          );

          resetTimer = null;

          return;
        }


        initializePostLength();

      }, 100);


    /*
     * Handle Shopify dynamic rendering.
     *
     * This only hides placeholder/update Shop Pay.
     * Once userHasSelectedPostLength is true,
     * it DOES NOT reset the selected radio.
     */
    const observer =
      new MutationObserver(
        function () {
          requestAnimationFrame(
            initializePostLength
          );
        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start
    );
  } else {
    start();
  }

})();

